// Omni-search query builders. v1 ships posts only; users and groups builders land here later.
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Query,
  QueryConstraint,
  Timestamp,
  where,
} from "firebase/firestore";

import { firestore } from "@/firebase/web";
import { Post } from "@/types/post";
import { mapDocToPost } from "@/utils/mapDocToPost.web";

export type SearchDateField = "createdAt" | "eventDate";

export type PostsSearchFilters = {
  country?: string;
  eventType?: string;
  dateField: SearchDateField;
  dateFrom?: Date | null;
  dateTo?: Date | null;
};

export const POSTS_SEARCH_LIMIT = 20;

export function hasActiveFilters(filters: PostsSearchFilters): boolean {
  return Boolean(
    filters.country ||
      filters.eventType ||
      filters.dateFrom ||
      filters.dateTo,
  );
}

function baseConstraints(filters: PostsSearchFilters): {
  constraints: QueryConstraint[];
  orderField: SearchDateField;
} {
  const constraints: QueryConstraint[] = [
    where("plateFollowersOnly", "==", false),
    where("groupId", "==", null),
  ];

  if (filters.country) {
    constraints.push(where("location.country", "==", filters.country));
  }

  if (filters.eventType) {
    constraints.push(where("eventType", "==", filters.eventType));
  }

  const hasDateRange = Boolean(filters.dateFrom || filters.dateTo);
  const orderField: SearchDateField = hasDateRange
    ? filters.dateField
    : "createdAt";

  if (filters.dateFrom) {
    constraints.push(
      where(orderField, ">=", Timestamp.fromDate(filters.dateFrom)),
    );
  }
  if (filters.dateTo) {
    const endOfDay = new Date(filters.dateTo);
    endOfDay.setHours(23, 59, 59, 999);
    constraints.push(where(orderField, "<=", Timestamp.fromDate(endOfDay)));
  }

  return { constraints, orderField };
}

export function buildPostsSearchQuery(filters: PostsSearchFilters): Query {
  const { constraints, orderField } = baseConstraints(filters);
  constraints.push(orderBy(orderField, "desc"));
  constraints.push(limit(POSTS_SEARCH_LIMIT));
  return query(collection(firestore, "posts"), ...constraints);
}

function buildUsernameQuery(
  filters: PostsSearchFilters,
  username: string,
): Query {
  const { constraints, orderField } = baseConstraints(filters);
  constraints.push(where("author.username", "==", username));
  constraints.push(orderBy(orderField, "desc"));
  constraints.push(limit(POSTS_SEARCH_LIMIT));
  return query(collection(firestore, "posts"), ...constraints);
}

function buildPlateQuery(
  filters: PostsSearchFilters,
  plateWithCountryPrefix: string,
): Query {
  const { constraints, orderField } = baseConstraints(filters);
  constraints.push(
    where("vehiclePlates", "array-contains", plateWithCountryPrefix),
  );
  constraints.push(orderBy(orderField, "desc"));
  constraints.push(limit(POSTS_SEARCH_LIMIT));
  return query(collection(firestore, "posts"), ...constraints);
}

const createdAtSeconds = (post: Post): number => {
  const ts = post.createdAt as any;
  if (!ts) return 0;
  if (typeof ts.seconds === "number") return ts.seconds;
  if (typeof ts.toDate === "function") return ts.toDate().getTime() / 1000;
  return 0;
};

export type SearchPostsArgs = {
  filters: PostsSearchFilters;
  searchText?: string;
  // Used as the plate country prefix when no country filter is set.
  fallbackCountry?: string | null;
};

export type SearchPostsResult = {
  posts: Post[];
  usernameMatches: number;
  plateMatches: number;
  plateSearchSkipped: boolean;
};

export async function searchPosts({
  filters,
  searchText,
  fallbackCountry,
}: SearchPostsArgs): Promise<SearchPostsResult> {
  const text = searchText?.trim();

  if (!text) {
    if (!hasActiveFilters(filters)) {
      return {
        posts: [],
        usernameMatches: 0,
        plateMatches: 0,
        plateSearchSkipped: false,
      };
    }
    const snap = await getDocs(buildPostsSearchQuery(filters));
    return {
      posts: snap.docs.map((d) => mapDocToPost(d)),
      usernameMatches: 0,
      plateMatches: 0,
      plateSearchSkipped: false,
    };
  }

  const country = (filters.country ?? fallbackCountry ?? "").toUpperCase();
  const plateKey = country ? `${country}.${text.toUpperCase()}` : null;

  const [usernameSnap, plateSnap] = await Promise.all([
    getDocs(buildUsernameQuery(filters, text)),
    plateKey ? getDocs(buildPlateQuery(filters, plateKey)) : Promise.resolve(null),
  ]);

  const byId = new Map<string, Post>();
  for (const d of usernameSnap.docs) {
    byId.set(d.id, mapDocToPost(d));
  }
  if (plateSnap) {
    for (const d of plateSnap.docs) {
      if (!byId.has(d.id)) {
        byId.set(d.id, mapDocToPost(d));
      }
    }
  }

  const merged = Array.from(byId.values())
    .sort((a, b) => createdAtSeconds(b) - createdAtSeconds(a))
    .slice(0, POSTS_SEARCH_LIMIT);

  return {
    posts: merged,
    usernameMatches: usernameSnap.size,
    plateMatches: plateSnap?.size ?? 0,
    plateSearchSkipped: !plateKey,
  };
}
